import React from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Box,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MediaType } from './MediaUploadSection';

interface MediaItem {
    id: string;
    type: 'capture' | 'upload' | 'placeholder';
    thumbnail?: string;
    videoUrl?: string;
    audioUrl?: string;
    name?: string;
    original_file_name?: string;
    file?: File;
}

interface MediaPreviewModalProps {
    open: boolean;
    onClose: () => void;
    mediaItem: MediaItem | null;
    mediaType: MediaType;
}

const MediaPreviewModal: React.FC<MediaPreviewModalProps> = ({
    open,
    onClose,
    mediaItem,
    mediaType,
}) => {
    const theme = useTheme();

    if (!mediaItem) return null;

    const getMediaUrl = () => {
        if (mediaType === 'video' && mediaItem.videoUrl) {
            return mediaItem.videoUrl;
        }
        if (mediaType === 'audio' && mediaItem.audioUrl) {
            return mediaItem.audioUrl;
        }
        if (mediaItem.file) {
            return URL.createObjectURL(mediaItem.file);
        }
        return mediaItem.thumbnail || '';
    };

    const renderMediaContent = () => {
        const mediaUrl = getMediaUrl();

        switch (mediaType) {
            case 'image':
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%',
                            maxWidth: '90vw',
                            maxHeight: '80vh',
                            overflow: 'auto',
                        }}
                    >
                        <img
                            src={mediaUrl}
                            alt={mediaItem.name || 'Image preview'}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                boxShadow: '0 2px 16px rgba(0,0,0,0.3)',
                                background: 'black',
                            }}
                        />
                    </Box>
                );

            case 'video':
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%',
                            maxWidth: '90vw',
                            maxHeight: '80vh',
                            overflow: 'auto',
                        }}
                    >
                        <video
                            key={mediaUrl}
                            src={mediaUrl}
                            controls
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                boxShadow: '0 2px 16px rgba(0,0,0,0.3)',
                                background: 'black',
                            }}
                            preload="metadata"
                        >
                            Your browser does not support the video tag.
                        </video>
                    </Box>
                );

            case 'audio':
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 4,
                            minWidth: 400,
                            maxWidth: '90vw',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 3,
                                fontFamily: theme.typography.fontFamily,
                                textAlign: 'center',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {(mediaItem.original_file_name || mediaItem.name || '').replace('_compressed', '')}
                        </Typography>
                        <audio
                            key={mediaUrl}
                            src={mediaUrl}
                            controls
                            style={{
                                width: '100%',
                                maxWidth: '400px',
                            }}
                            preload="metadata"
                        >
                            Your browser does not support the audio tag.
                        </audio>
                    </Box>
                );

            default:
                return (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography>Unsupported media type</Typography>
                    </Box>
                );
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth={false}
            sx={{
                '& .MuiDialog-paper': {
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                    color: 'white',
                    margin: 2,
                    position: 'relative',
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    minWidth: '320px',
                    minHeight: '280px',
                    width: 'auto',
                    height: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Close Button */}
            <IconButton
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    zIndex: 1,
                    '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                    },
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent
                sx={{
                    p: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    paddingBottom: mediaItem.name ? '80px' : '8px',
                }}
            >
                {renderMediaContent()}
            </DialogContent>

            {/* Media Info */}
            {(mediaItem.name || mediaItem.original_file_name) && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.8)',
                        p: 1.5,
                        textAlign: 'center',
                        maxHeight: '80px',
                        overflow: 'hidden',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'white',
                            fontFamily: theme.typography.fontFamily,
                            fontSize: '0.875rem',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                        }}
                    >
                        {(mediaItem.original_file_name || mediaItem.name || '').replace('_compressed', '')}
                    </Typography>
                </Box>
            )}
        </Dialog>
    );
};

export default MediaPreviewModal;
